import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { SnackbarKey, useSnackbar } from 'notistack';
import { type PropsWithChildren, useEffect, useState } from 'react';

import { PageLoadingFullscreen } from '../../components/general/PageLoadingFullscreen';
import { trpc } from '../../trpc';
import { ConnectToVpnWarning } from './ConnectVpnWarning';

let errorSnackbars: SnackbarKey[] = [];

export const backendReachableDelay = 20_000;

export const BackendReachable = ({ children }: PropsWithChildren) => {
  const [hasBeenIdleForLongTime, setHasBeenIdleForLongTime] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { inProgress } = useMsal();
  const isAuthenticating = inProgress !== InteractionStatus.None;

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setHasBeenIdleForLongTime(true);
    }, backendReachableDelay);
    return () => clearTimeout(timeoutId);
  }, []);

  /*
   * This ping serves two purposes:
   * - start backendLambda on app open to speed up first search results
   * - determine if the backend is reachable at all
   *   - this way, we can offer the user some troubleshooting ideas for the most common failures, like not being logged into VPN
   */
  const ping = trpc.ping.useQuery(undefined, {
    enabled: !isAuthenticating,
  });

  useEffect(() => {
    if (ping.isError && ping.error.data?.code !== 'UNAUTHORIZED') {
      errorSnackbars.push(
        enqueueSnackbar('Ein Backend-Fehler ist aufgetreten. Versuche, neu zu laden, oder wende dich ans Team VAMOS.', {
          variant: 'error',
          persist: true,
          preventDuplicate: true,
        }),
      );
    }
  }, [enqueueSnackbar, ping.error?.data?.code, ping.isError]);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- added during migration to ban-pdk. please solve issue or explain disable-reason here
    if (!isAuthenticating && ping.isLoading && hasBeenIdleForLongTime) {
      errorSnackbars.push(
        enqueueSnackbar(
          'Es dauert ungewöhnlich lange, um eine Antwort zu erhalten. Bist du mit dem Haufe-VPN verbunden?',
          {
            variant: 'error',
            persist: true,
            preventDuplicate: true,
          },
        ),
      );
    }
  }, [enqueueSnackbar, hasBeenIdleForLongTime, isAuthenticating, ping.isLoading]);

  useEffect(() => {
    if (ping.isSuccess) {
      errorSnackbars.forEach((snackbarKey) => closeSnackbar(snackbarKey));
      errorSnackbars = [];
    }
  }, [ping.isSuccess, closeSnackbar]);

  if (isAuthenticating) {
    return <PageLoadingFullscreen msg="Authentifizierung läuft..." />;
  }

  if (ping.error?.data?.code === 'UNAUTHORIZED') {
    return (
      <ConnectToVpnWarning message="Prüfe bitte mit der Haufe-IT, ob dein Microsoft-Account dazu berechtigt ist, auf VIVA zuzugreifen." />
    );
  }

  // Block the UI until ping (including token acquisition) finishes, so a later
  // MSAL loginRedirect cannot wipe user input from the search page.
  if (!ping.isSuccess && !ping.isError) {
    return <PageLoadingFullscreen msg="Wird geladen..." />;
  }

  return <>{children}</>;
};
